import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { Icon } from '@iconify/react/dist/iconify.js';
import NavHeader from "./NavHeader";
import LoadingSpinner from './LoadingSpinner';
import Avatar from './Avatar';

import { useAuth } from '../core/AuthContext';
import { useSemester } from '../core/SemesterContext';
import '../styles/UserProfile.css';
import '../styles/general.css';


// import StripsHead from './images/StripsHead.png';
import { URL } from '../core/config';
import { getDepartmentIcon, getDepartmentColor, getDepartmentName, getDepartmentId } from '../core/constants/departments';
import { useDepartments } from '../core/DepartmentsContext';

import { useDispatch } from 'react-redux';
import {
  canEditUserProfile,
  canViewGeneralMeetingAttendance,
  canViewUserContactDetails,
} from '../core/utils/permissionUtils';
import {
  getGeneralMeetingAttendanceStatus,
  getGeneralMeetingJustification,
  isGeneralMeetingPresent,
} from '../core/utils/generalMeetingUtils';
import { logoutUser } from '../core/store/actions/auth';
import { getRoleDisplayName } from '../core/utils/roleUtils';
import RolePositionEmailContacts from './profile/RolePositionEmailContacts';

const usersURL = URL + "/users";
const eventsURL = URL + "/events";
const DATE_FORMAT = 'dd/MM/yyyy';

function UserProfile() {

  const authContext = useAuth();
  const contextUser = authContext.user;

  const semesterContext = useSemester();
  const semester = semesterContext.currentSemester;
  const { departments: catalog } = useDepartments();

  const dispatch = useDispatch();

  const handleLogout = () => {
    dispatch(logoutUser());
  };

  const { userId } = useParams();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [organizedEvents, setOrganizedEvents] = useState([]);
  const [participatedEvents, setParticipatedEvents] = useState([]);
  const [generalMeetings, setGeneralMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userDetailsExpanded, setUserDetailsExpanded] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchUserAndEvents = async () => {
      if (!semester?._id || !userId) return;

      setLoading(true);
      setError(null);

      const canViewGmAttendance = canViewGeneralMeetingAttendance(contextUser, userId);

      try {
        const userResponse = await axios.get(usersURL + `/${userId}`);
        if (isMounted) setUser(userResponse.data);

        // Regular events (General Meetings are excluded by default here)
        const eventsResponse = await axios.get(`${eventsURL}/eventsbyuser/${userId}?semester=${semester._id}`);
        if (isMounted) setEvents(eventsResponse.data);

        if (canViewGmAttendance) {
          // All General Meetings this semester (not just this user's), so we can
          // show which ones they attended vs missed rather than just the ones
          // they attended.
          const generalMeetingsResponse = await axios.get(
            `${eventsURL}/list?semester=${semester._id}&onlyGeneralMeetings=true`,
            { params: { _t: Date.now() } },
          );
          if (isMounted) setGeneralMeetings(generalMeetingsResponse.data);
        } else if (isMounted) {
          setGeneralMeetings([]);
        }

      } catch (err) {
        if (isMounted) setError('Error fetching user or events data');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchUserAndEvents();

    return () => {
      isMounted = false;
    };
  }, [userId, semester?._id, contextUser, location.key]);


  useEffect(() => {
    const organized = events.filter(event => {
      //const isMO = event.mo && (event.mo === userId || (event.mo._id && event.mo._id === userId));
      const isMO = Array.isArray(event.mos) && event.mos.some(mo => (mo === userId || (mo._id && mo._id === userId)));
      const isVMO = Array.isArray(event.vmos) && event.vmos.some(vmo => (vmo === userId || (vmo._id && vmo._id === userId)));
      return isMO || isVMO;
    });

    const participated = events.filter(event => {
      const isParticipant = Array.isArray(event.participants) && event.participants.some(p => (p === userId || (p._id && p._id === userId)));
      //const isMO = event.mo && (event.mo === userId || (event.mo._id && event.mo._id === userId));
      const isMO = Array.isArray(event.mos) && event.mos.some(mo => (mo === userId || (mo._id && mo._id === userId)));
      const isVMO = Array.isArray(event.vmos) && event.vmos.some(vmo => (vmo === userId || (vmo._id && vmo._id === userId)));
      return isParticipant && !isMO && !isVMO;
    });

    setOrganizedEvents(organized);
    setParticipatedEvents(participated);
  }, [events, userId]);

  if (loading) return <div className="page-container"><LoadingSpinner message="Loading profile" /></div>;
  if (error) return (
  <div>
    <button className="button button-transparent-black" onClick={handleLogout}>
      <Icon icon="fluent:arrow-exit-20-filled" width="1.5em" height="1.5em" style={{ marginRight: '10px' }} />
      Log Out
    </button>
    <p>{error}</p>
  </div>
  );

  const formattedDate = user.birthDate ? format(new Date(user.birthDate), DATE_FORMAT) : '';

  function formatEventDateCard(dataString) {
    if (!dataString) return { day: '', month: '' };
    const date = new Date(dataString);
    return {
      day: format(date, 'dd'),
      month: format(date, 'MMM'),
    };
  }

  function renderEventCard(event) {
    const { day, month } = formatEventDateCard(event.date);

    return (
      <li className="events-container" key={event._id}>
        <div className="events-title">{event.name}</div>
        {event.departments?.length > 0 && (
          <div className="events-departments">
            {event.departments.map((department) => (
              <span
                key={getDepartmentId(department) || getDepartmentName(department, catalog)}
                className="user-item-department"
                style={{ '--department-color': getDepartmentColor(department, catalog) }}
              >
                <div className="dep-icon">{getDepartmentIcon(department, {}, catalog)}</div>
                <div className="dep-name">{getDepartmentName(department, catalog)}</div>
              </span>
            ))}
          </div>
        )}
        {event.meetingPoint && (
          <div className="events-location">{event.meetingPoint}</div>
        )}
        {(day || month) && (
          <div className="events-date">
            <span className="events-date-day">{day}</span>
            {month && <span className="events-date-month">{month}</span>}
          </div>
        )}
      </li>
    );
  }

  function isGeneralMeetingAttended(event) {
    return isGeneralMeetingPresent(event, userId);
  }

  function renderGeneralMeetingCard(event) {
    const { day, month } = formatEventDateCard(event.date);
    const status = getGeneralMeetingAttendanceStatus(event, userId);
    const justification = getGeneralMeetingJustification(event, userId);

    const statusLabel =
      status === 'attended'
        ? 'Attended'
        : status === 'justified'
          ? 'Justified absence'
          : 'Not attended';

    const statusIcon =
      status === 'attended'
        ? 'mdi:check-circle'
        : status === 'justified'
          ? 'mdi:check-decagram-outline'
          : 'mdi:close-circle-outline';

    return (
      <li
        className={`events-container general-meeting-card ${status}`}
        key={event._id}
      >
        <div className="events-title">{event.name}</div>
        <div className="general-meeting-attendance">
          <Icon icon={statusIcon} />
          {statusLabel}
        </div>
        {status === 'justified' && justification && (
          <div className="general-meeting-justification">{justification}</div>
        )}
        {event.meetingPoint && (
          <div className="events-location">{event.meetingPoint}</div>
        )}
        {(day || month) && (
          <div className="events-date">
            <span className="events-date-day">{day}</span>
            {month && <span className="events-date-month">{month}</span>}
          </div>
        )}
      </li>
    );
  }

  const attendedGeneralMeetingsCount = generalMeetings.filter(isGeneralMeetingAttended).length;
  const canViewGmAttendance = canViewGeneralMeetingAttendance(contextUser, userId);
  const generalMeetingAttendanceAverage = generalMeetings.length > 0
    ? ((attendedGeneralMeetingsCount / generalMeetings.length) * 100).toFixed(2)
    : null;

  return (
    <div className='page-container'>

      {/* Header */}
      <NavHeader />

      {/* Actual page */}
      <div className='user-profile-header'>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Avatar user={user} size={120} />
        </div>
        <h1 className='user-profile-title'>{user.name}</h1>

        <div className='user-profile-actions'>
          {canEditUserProfile(contextUser, userId) && (
            <Link to={`/users/${userId}/edit`} className="button user-profile-action">
              <Icon icon="mdi:pencil" className="user-profile-action-icon" />
              <span className="user-profile-action-label">Edit Profile</span>
            </Link>
          )}

          <Link to={`/users/${user._id}/details`} className="button user-profile-action">
            <Icon icon="mdi:information-outline" className="user-profile-action-icon" />
            <span className="user-profile-action-label">More Info</span>
          </Link>

          {userId === contextUser._id && (
            <button className="button user-profile-action" onClick={handleLogout}>
              <Icon icon="fluent:arrow-exit-20-filled" className="user-profile-action-icon" />
              <span className="user-profile-action-label">Log Out</span>
            </button>
          )}
        </div>
      </div>

      <div className='user-list-container user-profile-page'>
        <div className={`user-list-lateral-section ${userDetailsExpanded ? 'expanded' : 'collapsed'}`} style={{ padding: '10px' }}>
          <button
            type="button"
            className="user-profile-details-toggle"
            onClick={() => setUserDetailsExpanded((prev) => !prev)}
            aria-expanded={userDetailsExpanded}
          >
            <span className="user-profile-details-toggle-label">User Details</span>
            <Icon
              icon={userDetailsExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
              className="user-profile-details-toggle-icon"
            />
          </button>

          <div className="user-profile-details-content">
          <div className={"row section"} style={{ margin: '0', marginBottom: '30px' }}>
            <div className={"main-descriptions user-title"}>
              User Details
            </div>
            <p className='detail-info'><span className='detail-title'>Full Name:</span> {user.fullName} </p>
            {user.firstLastName && <p className='detail-info'><span className='detail-title'>Short Name:</span> {user.firstLastName}</p>}
            {user.nickname && <p className='detail-info'><span className='detail-title'>Nickname:</span> {user.nickname}</p>}
            <p className='detail-info'><span className='detail-title'>Birthday:</span> {formattedDate} </p>
            <p className='detail-info'><span className='detail-title'>Member:</span> <span style={{ fontStyle: 'italic' }}>{user.membershipType}</span></p>
            <p className='detail-info detail-info-departments'>
              <span className='detail-title'>Departments:</span>
              <span className='user-item-departments'>
                {(user.departments || []).map((department, index) => (
                  <span
                    key={getDepartmentId(department) || index}
                    className='user-item-department'
                    style={{ '--department-color': getDepartmentColor(department, catalog) }}
                  >
                    <div className='dep-icon'>{getDepartmentIcon(department, {}, catalog)}</div>
                    <div className='dep-name'>{getDepartmentName(department, catalog)}</div>
                  </span>
                ))}
              </span>
            </p>
            {user.roles && user.roles.length > 0 && (
              <p className='detail-info'>
                <span className='detail-title'>Role(s):</span>{' '}
                {user.roles.map((role) => getRoleDisplayName(role)).filter(Boolean).join(', ')}
              </p>
            )}
           {user.suspension?.isSuspended ? (
             <p className='detail-info'><span className='detail-title'>Status:</span> Suspended</p>
           ) : (
             <p className='detail-info'><span className='detail-title'>Status:</span> Active</p>
           )}
           {canViewGmAttendance && (
             <p className='detail-info'>
               <span className='detail-title'>General Meeting attendance:</span>{' '}
               {generalMeetingAttendanceAverage != null
                 ? `${generalMeetingAttendanceAverage}%`
                 : 'No meetings this semester'}
             </p>
           )}
          </div>

          <div className={"row section"} style={{ margin: '0', marginBottom: '30px' }}>
            <div className={"main-descriptions user-title"}>
              Contacts
            </div>

            {user.esnAccountUsername && (
              <div className='detail-info'>
                <span className='detail-title'><Icon icon="mdi:account-circle-outline" className='detail-icon' /> ESN Account</span>
                <p style={{ paddingLeft: '23px'}}>
                  <a
                    href={`https://accounts.esn.org/user/${encodeURIComponent(user.esnAccountUsername.replace(/\./g, ''))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {user.esnAccountUsername}
                  </a>
                </p>
              </div>
            )}
            {user.institutionalEmail && (
              <div className='detail-info'>
                <span className='detail-title'><Icon icon="dashicons:email" className='detail-icon' /> ESN email</span>
                <p style={{ paddingLeft: '23px' }}>{user.institutionalEmail}</p>
              </div>
            )}
            <RolePositionEmailContacts roles={user.roles} />
            {(canViewUserContactDetails(contextUser, userId) || !user.institutionalEmail) && (
              <div className='detail-info'>
                <span className='detail-title'><Icon icon="dashicons:email" className='detail-icon' /> Personal email</span>
                <p style={{ paddingLeft: '23px' }}>{user.personalEmail}</p>
              </div>
            )}

            {canViewUserContactDetails(contextUser, userId) && (
              <div className='detail-info'>
                <span className='detail-title'><Icon icon="ic:baseline-phone" className='detail-icon' /> Phone Number</span>
                <p style={{ paddingLeft: '23px' }}>{user.phone}</p>
              </div>
            )}
          </div>

          {/* TODO: Implement this later */}
          {/* <div className={"row section"} style={{ margin: '0', marginBottom: '30px', width: '100%' }}>
            <div className={"main-descriptions user-title"}>
              Achievements
            </div>
            {/* <div className={"achievements-section"}>
              <div className={"achievements-container"}>
                <img src={StripsHead} className={"achievement-image"} />
                <div className={"achievement-date"}>
                  2023-2024
                </div>
              </div>
            </div>
          </div>*/}
          </div>
        </div>

        <div className='user-list-main-section' style={{ padding: '10px' }}>
          <div className={"row section"} style={{ margin: '0', marginBottom: '30px' }}>
            <div className={"main-descriptions user-title"}>
              Organized Events
            </div>
            {organizedEvents.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', color: '#000', margin: '16px 0' }}>
                <Icon icon="mdi:calendar-remove" width="2em" height="2em" style={{ marginRight: '10px' }} />
                <span>No organized events yet</span>
              </div>
            ) : (
              <ul className="events-list">
                {organizedEvents.map((event) => renderEventCard(event))}
              </ul>
            )}
          </div>

          <div className={"row section"} style={{ margin: '0', marginBottom: '30px' }}>
            <div className={"main-descriptions user-title"}>
              Participation in Events
            </div>
            {participatedEvents.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', color: '#000', margin: '16px 0' }}>
                <Icon icon="mdi:calendar-remove" width="2em" height="2em" style={{ marginRight: '10px' }} />
                <span>No participation in events yet</span>
              </div>
            ) : (
              <ul className="events-list">
                {participatedEvents.map((event) => renderEventCard(event))}
              </ul>
            )}
          </div>

          {canViewGmAttendance && (
          <div className={"row section"} style={{ margin: '0', marginBottom: '30px' }}>
            <div className="general-meetings-heading">
              <div className={"main-descriptions user-title"}>
                General Meetings
              </div>
              <span className="general-meetings-attendance-average">
                {generalMeetingAttendanceAverage != null
                  ? `${generalMeetingAttendanceAverage}% average attendance`
                  : 'No meetings this semester'}
              </span>
              {generalMeetingAttendanceAverage != null && (
                <span className="general-meetings-count-badge">
                  {attendedGeneralMeetingsCount}/{generalMeetings.length} attended
                </span>
              )}
            </div>
            {generalMeetings.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', color: '#000', margin: '16px 0' }}>
                <Icon icon="mdi:calendar-remove" width="2em" height="2em" style={{ marginRight: '10px' }} />
                <span>No general meetings this semester yet</span>
              </div>
            ) : (
              <ul className="events-list">
                {generalMeetings.map((event) => renderGeneralMeetingCard(event))}
              </ul>
            )}
          </div>
          )}
        </div>


      </div>

    </div>
  );
}

export default UserProfile;
